const GOOGLE_IDENTITY_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const YOUTUBE_UPLOAD_SCOPE = 'https://www.googleapis.com/auth/youtube.upload';
const YOUTUBE_UPLOAD_URL =
  'https://www.googleapis.com/upload/youtube/v3/videos?uploadType=resumable&part=snippet,status';

export type YouTubePrivacyStatus = 'private' | 'unlisted' | 'public';

interface GoogleTokenResponse {
  access_token?: string;
  error?: string;
  error_description?: string;
}

interface GoogleTokenClient {
  requestAccessToken: (overrideConfig?: { prompt?: string }) => void;
}

interface GoogleIdentityApi {
  accounts: {
    oauth2: {
      initTokenClient: (config: {
        client_id: string;
        scope: string;
        callback: (response: GoogleTokenResponse) => void;
        error_callback?: (error: unknown) => void;
      }) => GoogleTokenClient;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityApi;
  }
}

export interface PublishToYouTubeParams {
  blob: Blob;
  title: string;
  description?: string;
  tags?: string[];
  privacyStatus: YouTubePrivacyStatus;
  madeForKids: boolean;
  publishAt?: Date | null;
  onProgress?: (progress: number) => void;
}

export interface YouTubePublishResult {
  id: string;
  url: string;
  studioUrl: string;
  channelId?: string;
  channelTitle?: string;
  privacyStatus?: YouTubePrivacyStatus;
  uploadStatus?: string;
}

const getYouTubeClientId = () =>
  import.meta.env.VITE_GOOGLE_CLIENT_ID ||
  import.meta.env.VITE_YOUTUBE_CLIENT_ID;

const loadGoogleIdentityScript = () =>
  new Promise<void>((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${GOOGLE_IDENTITY_SCRIPT_URL}"]`
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), {
        once: true,
      });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Unable to load Google sign-in.')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = GOOGLE_IDENTITY_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Unable to load Google sign-in.'));
    document.head.appendChild(script);
  });

const requestYouTubeAccessToken = async () => {
  const clientId = getYouTubeClientId();

  if (!clientId) {
    throw new Error(
      'Missing Google OAuth client id. Set VITE_GOOGLE_CLIENT_ID in the frontend environment.'
    );
  }

  await loadGoogleIdentityScript();

  if (!window.google?.accounts?.oauth2) {
    throw new Error('Google sign-in is not available.');
  }

  return new Promise<string>((resolve, reject) => {
    const tokenClient = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: YOUTUBE_UPLOAD_SCOPE,
      callback: (response) => {
        if (response.error) {
          reject(
            new Error(
              response.error_description ||
                `YouTube authorization failed: ${response.error}`
            )
          );
          return;
        }

        if (!response.access_token) {
          reject(new Error('YouTube authorization did not return a token.'));
          return;
        }

        resolve(response.access_token);
      },
      error_callback: () => {
        reject(new Error('YouTube authorization was cancelled or blocked.'));
      },
    });

    tokenClient.requestAccessToken({ prompt: 'consent select_account' });
  });
};

const getYouTubeErrorMessage = async (response: Response) => {
  try {
    const errorData = await response.json();
    return (
      errorData?.error?.message ||
      errorData?.message ||
      `YouTube upload failed with status ${response.status}`
    );
  } catch {
    return `YouTube upload failed with status ${response.status}`;
  }
};

const startResumableUpload = async (
  accessToken: string,
  params: PublishToYouTubeParams
) => {
  const tags = params.tags?.filter(Boolean) ?? [];
  const metadata = {
    snippet: {
      title: params.title,
      description: params.description ?? '',
      ...(tags.length > 0 ? { tags } : {}),
    },
    status: {
      privacyStatus: params.publishAt ? 'private' : params.privacyStatus,
      selfDeclaredMadeForKids: params.madeForKids,
      ...(params.publishAt ? { publishAt: params.publishAt.toISOString() } : {}),
    },
  };

  const response = await fetch(YOUTUBE_UPLOAD_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json; charset=UTF-8',
      'X-Upload-Content-Length': String(params.blob.size),
      'X-Upload-Content-Type': params.blob.type || 'video/webm',
    },
    body: JSON.stringify(metadata),
  });

  if (!response.ok) {
    throw new Error(await getYouTubeErrorMessage(response));
  }

  const uploadUrl = response.headers.get('Location');
  if (!uploadUrl) {
    throw new Error('YouTube did not return an upload URL.');
  }

  return uploadUrl;
};

const uploadVideoBlob = (
  uploadUrl: string,
  accessToken: string,
  blob: Blob,
  onProgress?: (progress: number) => void
) =>
  new Promise<YouTubePublishResult>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', uploadUrl);
    request.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    request.setRequestHeader('Content-Type', blob.type || 'video/webm');

    request.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    request.onload = () => {
      if (request.status < 200 || request.status >= 300) {
        reject(
          new Error(
            request.responseText ||
              `YouTube upload failed with status ${request.status}`
          )
        );
        return;
      }

      try {
        const response = JSON.parse(request.responseText);
        if (!response.id) {
          reject(new Error('YouTube upload finished without a video id.'));
          return;
        }

        resolve({
          id: response.id,
          url: `https://www.youtube.com/watch?v=${response.id}`,
          studioUrl: `https://studio.youtube.com/video/${response.id}/edit`,
          channelId: response.snippet?.channelId,
          channelTitle: response.snippet?.channelTitle,
          privacyStatus: response.status?.privacyStatus,
          uploadStatus: response.status?.uploadStatus,
        });
      } catch {
        reject(new Error('Unable to read the YouTube upload response.'));
      }
    };

    request.onerror = () => reject(new Error('YouTube upload failed.'));
    request.send(blob);
  });

export const publishVideoToYouTube = async (
  params: PublishToYouTubeParams
) => {
  const accessToken = await requestYouTubeAccessToken();
  const uploadUrl = await startResumableUpload(accessToken, params);
  return uploadVideoBlob(uploadUrl, accessToken, params.blob, params.onProgress);
};
